import { IBleClient } from './IBleClient';
import { IQConnect } from './IQConnect';

export class IQConnectEmulator implements IBleClient {

    private sessionLogInterval: any;
    private nextPulse: any;
    private numberOfPulses: number = 0;
    private triggerPulled: boolean = false;

    private amplitudeMap = new Map([
        [4, { amp: 1.44, duration: 103.4 }],
        [4.5, { amp: 1.66, duration: 101 }],
        [5, { amp: 1.88, duration: 98.6 }],
        [5.5, { amp: 2.1, duration: 96.2 }],
        [6, { amp: 2.32, duration: 93.8 }],
        [6.5, { amp: 2.54, duration: 91.4 }],
        [7, { amp: 2.76, duration: 89 }],
        [7.5, { amp: 2.98, duration: 86.6 }],
        [8, { amp: 3.2, duration: 84.2 }],
        [8.5, { amp: 3.42, duration: 81.8 }],
        [9, { amp: 3.64, duration: 79.4 }],
        [9.5, { amp: 3.86, duration: 77 }],
        [10, { amp: 4.08, duration: 74.6 }],
        [10.5, { amp: 4.3, duration: 72.2 }],
        [11, { amp: 4.52, duration: 69.8 }],
        [11.5, { amp: 4.74, duration: 67.4 }],
        [12, { amp: 4.96, duration: 65 }]
    ]);

    private dosage: number = 0;
    private triggers: number = 0;
    private forceLevel: number = 1; //1 = 6
    private thrusts: number = 0;

    private deviceConnected = false;
    private notificationCallback: ((value: DataView) => void) | null = null;

    async initialize(): Promise<void> {
    }

    async requestDevice(options: any): Promise<any> {
        return { deviceId: '94F0EE4D-E198-49A3-F237-08DD920468CE', name: 'Simulated Device' };
    }

    async requestLEScan(options: any, callback: (result: any) => void): Promise<void> {
        setTimeout(() => {
            callback({ device: { deviceId: '94F0EE4D-E198-49A3-F237-08DD920468CE', name: 'Simulated Device' } });
        }, 2000);
    }

    async stopLEScan(): Promise<void> {
    }

    async connect(deviceId: string, disconnectCallback: () => void): Promise<void> {
        this.deviceConnected = true;
        this.sessionLogInterval = setInterval(() => {
            this.sendSessionLog();
        }, 5000);
    }

    async disconnect(deviceId: string): Promise<void> {
        this.deviceConnected = false;
        if(this.sessionLogInterval){
            clearInterval(this.sessionLogInterval);
        }
    }

    async startNotifications(
        deviceId: string,
        service: string,
        characteristic: string,
        callback: (value: DataView) => void
    ): Promise<void> {
        this.notificationCallback = callback;
    }

    async write(
        deviceId: string,
        service: string,
        characteristic: string,
        value: DataView
    ): Promise<void> {
        if (this.notificationCallback) {
            const data = new Uint8Array(value.buffer);
            const dataString = this.dataToString(data);
            setTimeout(() => {
                    switch (data[0]) {
                    case IQConnect.REQ_SESSION_LOG:
                        this.sendSessionLog();
                        break;
                    case IQConnect.REQ_PIC_SETTINGS:
                        this.sendPicSettings();
                        break;
                    case IQConnect.REQ_DISPLAY_BRD_SN:
                        this.sendBoardSnResponse(IQConnect.REQ_DISPLAY_BRD_SN, 'SIM-DISPLAY-BRD');
                        break;
                    case IQConnect.REQ_MAIN_BRD_SN:
                        this.sendBoardSnResponse(IQConnect.REQ_MAIN_BRD_SN, 'SIM-MAIN-BRD');
                        break;
                    case IQConnect.REQ_BOARD_FIRMWARE_VERSION:
                        this.sendBoardFirmwareVersions();
                        break;
                }
            }, 500);
        }
    }

    private sendBoardSnResponse(commandByte: number, ascii: string) {
        const padded = ascii.padEnd(IQConnect.FACTORY_SERIAL_FIELD_LENGTH, '\0').slice(0, IQConnect.FACTORY_SERIAL_FIELD_LENGTH);
        const encoded = new TextEncoder().encode(padded);
        const payload = new Uint8Array(1 + IQConnect.FACTORY_SERIAL_FIELD_LENGTH);
        payload[0] = commandByte;
        payload.set(encoded, 1);
        this.notificationCallback!(new DataView(payload.buffer));
    }

    private sendBoardFirmwareVersions() {
        const displayCenti = 208;
        const mainCenti = 205;
        const payload = new Uint8Array([
            IQConnect.REQ_BOARD_FIRMWARE_VERSION,
            displayCenti & 0xff,
            (displayCenti >> 8) & 0xff,
            mainCenti & 0xff,
            (mainCenti >> 8) & 0xff,
        ]);
        this.notificationCallback!(new DataView(payload.buffer));
    }

    sendSerialNumber() {
        const serialNumber = new Uint8Array([0xF0, 0x31, 0x32, 0x33, 0x34, 0x35, 0x36, 0x37, 0x38, 0x39, 0x30, 0x31, 0x00, 0x00, 0x00]);
        this.notificationCallback!(new DataView(serialNumber.buffer));
    }

    simulateTriggerCommand(): void {
        this.triggers++;
        this.triggerPulled = true;
        this.dosage = 0;
        this.numberOfPulses = Math.floor(Math.random() * (50 - 10 + 1)) + 10; // Random pulses between 10 and 50
        let totalPulsesSent = 0;
        let pulsesForCurrentFrequency = Math.floor(Math.random() * (20 - 3 + 1)) + 3; // Random batch between 3 and 20
        let pulsesInCurrentBatch = 0;

        let frequencyIndex = Math.floor(Math.random() * 16); // Random initial frequency index (0 to 15)

        // Get the duration for the selected frequency index
        let amplitudeEntry = this.amplitudeMap.get(frequencyIndex) || { duration: 100 }; // Default to 100ms if missing

        const sendPulse = () => {
            if (!this.triggerPulled || totalPulsesSent >= this.numberOfPulses) {
                clearInterval(this.nextPulse);
                if (this.triggerPulled) {
                    this.simulateSendAdjustSuccess(); // Send success message when done
                }
                return;
            }

            // Send frequency pulse
            this.simulateSendFrequency(frequencyIndex);
            totalPulsesSent++;
            pulsesInCurrentBatch++;

            // If the number of pulses in this batch is reached, change the frequency
            if (pulsesInCurrentBatch >= pulsesForCurrentFrequency) {
                pulsesInCurrentBatch = 0; // Reset batch counter
                pulsesForCurrentFrequency = Math.floor(Math.random() * (50 - 10 + 1)) + 10; // Pick new batch size
                frequencyIndex = Math.floor(Math.random() * 16); // Pick a new frequency index

                // Update the duration for the new frequency index
                amplitudeEntry = this.amplitudeMap.get(frequencyIndex) || { duration: 100 };
                // Restart the interval with the new duration
                clearInterval(this.nextPulse);

                this.nextPulse = setInterval(sendPulse, amplitudeEntry.duration);

            }
        };

        // Schedule pulses at the correct interval based on initial frequency's duration

        this.nextPulse = setInterval(sendPulse, amplitudeEntry.duration);

    }

    simulateSendFrequency(frequencyIndex: number): void {
        this.dosage++;
        this.thrusts++;
        const frequencyData = new Uint8Array([IQConnect.CMD_FREQUENCY, 0x01, 0x01, frequencyIndex, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00]);
        this.notificationCallback!(new DataView(frequencyData.buffer));
    }

    simulateSendAdjustSuccess(): void {
        this.playPingSound();
        const adjustSuccess = new Uint8Array([IQConnect.CMD_ADJUST_SUCCESS, 0x01, 0x01, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00]);
        this.notificationCallback!(new DataView(adjustSuccess.buffer));
        setTimeout(() => { this.simulateTriggerRelease() }, 100);
    }

    private playPingSound(): void {
        return;
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)(); // Ensure compatibility
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.type = 'square'; // Old PC speaker used square waves
        oscillator.frequency.setValueAtTime(1000, audioContext.currentTime); // 1000 Hz = Classic beep

        gainNode.gain.setValueAtTime(0.5, audioContext.currentTime); // Louder beep (0.5 = 50% volume)
        gainNode.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.2); // Quick fade-out

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.2); // Play for 200ms (short beep)
    }

    simulateTriggerRelease(): void {
        if (this.notificationCallback) {
            setTimeout(() => {
                this.triggerPulled = false;
                const triggerReleaseData = new Uint8Array([IQConnect.CMD_TRIGGER_RELEASED, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00]);
                this.notificationCallback!(new DataView(triggerReleaseData.buffer));
                setTimeout(() => {
                    this.simulatePreload(0);
                }, 10);
            }, 1);
        }
    }

    simulateDoubleTrigger(): void {
        if (this.notificationCallback) {
            const doubleTriggerData = new Uint8Array([IQConnect.CMD_DOUBLE_TRIGGER]);
            setTimeout(() => {
                this.notificationCallback!(new DataView(doubleTriggerData.buffer));
            }, 100);
        }
    }

    simulatePreload(level: number): void {
        if (this.notificationCallback) {
            setTimeout(() => {
                /*
                     [17][01][07][02][00][00][00][00][00][04]
                     CMD
                     uint8_t soh;
                     uint8 cmd;
                     uint8_t level;
                     uint8_t empty[5];
                     uint8_t eot;
                 */
                const preloadData = new Uint8Array([0x17, 0x01, 0x07, level, 0x00, 0x00, 0x00, 0x00, 0x00, 0x04]);
                this.notificationCallback!(new DataView(preloadData.buffer));
            }, 1);
        }
    }

    sendSessionLog(): void {

        // Convert numbers to 2-byte (uint16_t) representations
        const triggersBytes = this.getUShortBytes(this.triggers);
        const dosageBytes = this.getUShortBytes(this.dosage);
        const thrustsBytes = this.getUShortBytes(this.thrusts);

        // Randomly pick one of the three byte pairs for positions 8 and 9
        const randomBytePairs = [
            [0x7A, 0x00], //Less than 10°C
            [0xC3, 0x00], //45°C
            [0x14, 0x01], //85°C
            [0x34, 0x01], //Greater than 100°C
        ];
        const randomPair = randomBytePairs[Math.floor(Math.random() * randomBytePairs.length)];

        const sessionLog = new Uint8Array([
            IQConnect.CMD_SESSION_LOG,
            0x00, 0x00,  // Patients (not used)
            triggersBytes[0], triggersBytes[1],  // Triggers (2 bytes)
            dosageBytes[0], dosageBytes[1],  // Dosage (2 bytes)
            thrustsBytes[0], thrustsBytes[1],
            randomPair[0], randomPair[1],  // Solenoid temperature (fixed format)
        ]);

        this.notificationCallback!(new DataView(sessionLog.buffer));
    }

    // Helper method to convert a number to 2-byte representation
    private getUShortBytes(value: number): Uint8Array {
        return new Uint8Array([
            value & 0xff,       // Low byte (LSB)
            (value >> 8) & 0xff // High byte (MSB)
        ]);
    }

    sendPicSettings() {
        const picSettings = new Uint8Array([IQConnect.CMD_PIC_SETTING, 0x01, 0x02, 0x00, 0x01, this.getRandomForceIndex(), 0x00, 0x00, 0x00, 0x04]);
        this.notificationCallback!(new DataView(picSettings.buffer));
    }

    private getRandomForceIndex(): number {
        // 50/50 split between valid (0-5 => 25..400) and clearly invalid values to exercise validation paths
        const sendValid = Math.random() < 0.5;
        if (sendValid) {
            return Math.floor(Math.random() * 6); // 0-5
        }

        const invalidPool = [6, 7, 8, 9, 15, 31];
        return invalidPool[Math.floor(Math.random() * invalidPool.length)];
    }

    private dataToString(data: Uint8Array): string {
        return Array.from(data)
            .map(byte => `${byte.toString(16).toUpperCase().padStart(2, '0')}`)
            .join('');
    }

    setForce(forceLevel: number) {
        if (forceLevel <= 0 || forceLevel > 6)
            throw Error("invalid level");
        this.forceLevel = forceLevel;
        this.sendPicSettings();
    }
}
